import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const STATUS = { pending: "Pago pendiente de validación", validated: "Pago validado", rejected: "Pago rechazado" };

export default function GuestPurchase() {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { user, register, checking } = useAuth();
    const token = location.hash.slice(1);
    const [purchase, setPurchase] = useState(null);
    const [error, setError] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let active = true;
        setError("");
        if (!token) { setError("Abre el enlace privado completo que recibiste al comprar."); return; }
        api.post("/purchases/" + id + "/lookup", { token })
            .then(({ data }) => { if (active) setPurchase(data); })
            .catch((err) => { if (active) setError(formatApiError(err)); });
        return () => { active = false; };
    }, [id, token]);

    const claim = async (event) => {
        event.preventDefault();
        setBusy(true);
        try {
            if (!user) await register(purchase.user_email, password, purchase.user_name);
            await api.post("/purchases/" + id + "/claim", { token });
            toast.success("Compra vinculada a tu cuenta");
            navigate("/dashboard");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    const saveReceipt = () => {
        const text = [
            "Constancia de compra - RifaPerú",
            "Compra: " + purchase.id,
            "Rifa: " + purchase.raffle_title,
            "Boletos: " + purchase.ticket_numbers.join(", "),
            "Monto: S/ " + purchase.amount.toFixed(2),
            "Estado: " + STATUS[purchase.status],
            "Enlace privado de consulta: " + window.location.href,
            "Guarda este enlace y no lo compartas. Permite consultar tu compra.",
        ].join("\n");
        const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
        const link = document.createElement("a");
        link.href = url;
        link.download = "compra-" + purchase.id + ".txt";
        link.click();
        URL.revokeObjectURL(url);
    };

    if (error) return <div className="max-w-xl mx-auto p-8"><p role="alert">{error}</p><Link to="/rifas" className="underline">Ver rifas</Link></div>;
    if (!purchase || checking) return <div className="flex justify-center p-20"><Loader2 className="animate-spin" /></div>;

    return (
        <div className="max-w-2xl mx-auto px-4 py-12 space-y-6" data-testid="guest-purchase-page">
            <div className="brut-card p-6 space-y-4">
                <h1 className="font-display font-black text-3xl">Tu compra está registrada</h1>
                <p className="font-bold">{STATUS[purchase.status]}</p>
                <p>Rifa: <Link className="underline" to={"/rifas/" + purchase.raffle_id}>{purchase.raffle_title}</Link></p>
                <p>Boletos: {purchase.ticket_numbers.map((n) => "#" + n).join(", ")}</p>
                <p>Total: S/ {purchase.amount.toFixed(2)}</p>
                <p className="text-sm">Comprador: {purchase.user_name} · {purchase.user_email}</p>
                <p className="text-sm">Teléfono: {purchase.phone}</p>
                <p className="text-sm whitespace-pre-line">Envío del premio: {purchase.shipping_address}</p>
                {purchase.is_winner ? <div className="border-2 border-ink bg-mint p-4 font-bold">¡Ganaste {purchase.raffle_prize}! Boleto #{purchase.winner_number}. Te contactaremos para coordinar el envío.</div>
                    : purchase.raffle_status === "drawn" && <p>Número ganador: #{purchase.winner_number}</p>}
                <p className="text-sm">Guarda tu enlace privado para volver a consultar el pago y el resultado del sorteo sin iniciar sesión. No lo compartas.</p>
                <input aria-label="Enlace privado de consulta" className="brut-input text-xs" readOnly value={window.location.href} onFocus={(e) => e.target.select()} />
                <button className="brut-btn" onClick={saveReceipt}>Descargar constancia y enlace</button>
            </div>
            {!purchase.user_id ? (
                <form onSubmit={claim} className="brut-card p-6 space-y-4">
                    <h2 className="font-display font-black text-2xl">{user ? "Guardar en mi cuenta" : "Crear cuenta (opcional)"}</h2>
                    <p className="text-sm">Consulta tus compras y premios desde tu panel. Tu compra ya está registrada aunque no crees una cuenta.</p>
                    {!user && <>
                        <p className="text-sm">Correo de la cuenta: <strong>{purchase.user_email}</strong></p>
                        <label className="brut-label" htmlFor="new-password">Elige una contraseña</label>
                        <input id="new-password" autoComplete="new-password" type="password" required minLength={6} maxLength={72} className="brut-input" value={password} onChange={(e) => setPassword(e.target.value)} />
                    </>}
                    {user && user.email !== purchase.user_email ? <p role="alert">Para vincular esta compra, inicia sesión con {purchase.user_email}.</p>
                        : <button disabled={busy} className="brut-btn brut-btn-primary">{busy ? "Guardando..." : user ? "Vincular compra a mi cuenta" : "Crear cuenta y guardar compra"}</button>}
                    {!user && <p className="text-sm">¿Ya tienes una cuenta? <Link to="/login" state={{ from: location.pathname + location.hash }} className="underline">Inicia sesión con el correo de la compra</Link>.</p>}
                </form>
            ) : user && <Link className="brut-btn" to="/dashboard">Ver mi panel</Link>}
            <Link className="underline block" to="/rifas">Seguir viendo rifas</Link>
        </div>
    );
}

